import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "sr-only" }
const _hoisted_2 = {
  key: 0,
  class: "q-ma-sm"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "word-wrap" }
const _hoisted_5 = { class: "text-weight-medium" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { class: "text-weight-medium wrap-all" }
const _hoisted_8 = { class: "q-gutter-xs" }

import type { PropType } from 'vue';
import { ref, toRefs, computed } from 'vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal libs
import { IUser, IBackendDate } from '@shared/interface/model.interface';
import { RoleEnum } from '@shared/enum/role.enum';
import { $t } from 'src/i18n/utils';
import { UserDto } from '@data/dto/user-dto';
import {
  convertDateToLiterral,
  convertBackendDateToDate,
} from '@shared/lib/date.lib';
import { UserStatusEnum } from '@shared/enum/user.enum';
import { IQSelectOption } from '@shared/interface/quasar.interface';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';

/**
 * Viewmodel
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'QAdminUserList',
  props: {
  title: { type: String, required: true },
  users: { type: Object as PropType<Array<IUser>>, required: true },
  status: { type: String, required: true },
},
  emits: [
  'selected-user',
  'show-roles-dialog',
  'show-delete-dialog',
  'show-update-dialog',
  'show-password-dialog',
  'show-accept-dialog',
  'show-reject-dialog',
  'show-resend-validation-link-dialog'
],
  setup(__props, { emit: __emit }) {

// Vendor
const { isMobile } = useGlobalVM();

/**
 * Emits
 */
const emits = __emit;

/**
 * Reactive datas
 */
const selectedUser = ref<IUser>(new UserDto());
const selectedRoleFilter = ref<IQSelectOption<RoleEnum> | undefined>(undefined);

/**
 * Properties
 */
const props = __props;
const { users } = toRefs(props);

/**
 * Computed data
 */
const isOnline = computed(() => sessionManager.isOnline.value === true);
const hasToDisable = computed<boolean>(
  () => !isOnline.value,
);
const connectedUser = computed(() => sessionManager.currentUser.value);
const canValidateUser = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_USER_VALIDATE) === true,
);
const isSuperAdmin = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN) === true,
);
const isAdmin = computed<boolean>(
  () => connectedUser.value?.roles?.includes(RoleEnum.ROLE_ADMIN) === true,
);
const roleList = computed<IQSelectOption<string>[]>(() => {
  const tempArray: IQSelectOption<string>[] = [];
  const stringKeys = Object.keys(RoleEnum).filter((v) => isNaN(Number(v)));
  stringKeys.forEach((key) => {
    if (key !== RoleEnum.ROLE_LOCAL) {
      tempArray.push({
        label: $t(`users.rolesShort.${key}`),
        value: key,
      });
    }
  });

  tempArray.sort((a, b) => a.label.localeCompare(b.label));
  tempArray.splice(0, 0, {
    label: 'Tous',
    value: undefined,
  });
  return tempArray;
});

/**
 * Methods
 */
function isUserAdmin(user: IUser) {
  return user?.roles?.includes(RoleEnum.ROLE_ADMIN) === true;
}

function isUserSuperAdmin(user: IUser) {
  return user?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN) === true;
}

function formatUserDate(backendDate?: IBackendDate): string {
  if (backendDate != null) {
    return `Connecté le ${convertDateToLiterral(
      convertBackendDateToDate(backendDate),
    )}`;
  } else {
    return 'Jamais connecté';
  }
}

function formatSignupLinkExpiration(backendDate?: IBackendDate): string {
  if (backendDate != null) {
    return `Expire le ${convertDateToLiterral(convertBackendDateToDate(backendDate))}`
  } else {
    return 'inconnu';
  }
}

function formatSignupUser(backendDate?: IBackendDate): string {
  if (backendDate != null) {
    return `Inscrit le ${convertDateToLiterral(
      convertBackendDateToDate(backendDate),
    )}`;
  } else {
    return 'Ajouté manuellement';
  }
}

function onShowRolesDialog(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-roles-dialog');
  }
}

function onShowDeleteUserDialog(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-delete-dialog');
  }
}

function onShowUpdateDialog(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-update-dialog');
  }
}

function onShowGeneratePasswordDialog(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-password-dialog');
  }
}

function onShowAcceptDialog(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-accept-dialog');
  }
}

function onShowRejectDialog(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-reject-dialog');
  }
}

function onShowResendValidationLink(email?: string) {
  if (email != null) {
    selectedUser.value =
      users.value.find((u) => u.email === email) || new UserDto();
    emits('selected-user', selectedUser.value);
    emits('show-resend-validation-link-dialog');
  }
}

return (_ctx: any,_cache: any) => {
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_select = _resolveComponent("q-select")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_menu = _resolveComponent("q-menu")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_expansion_item = _resolveComponent("q-expansion-item")!
  const _component_q_list = _resolveComponent("q-list")!

  return (_openBlock(), _createBlock(_component_q_list, {
    bordered: "",
    class: "rounded-borders q-ma-xs",
    role: "list"
  }, {
    default: _withCtx(() => [
      _createElementVNode("h3", _hoisted_1, _toDisplayString(__props.title) + " (" + _toDisplayString(_unref(users).length) + ")", 1 /* TEXT */),
      _createVNode(_component_q_expansion_item, {
        "expand-separator": "",
        label: `${__props.title} (${_unref(users).length})`,
        "header-class": "bg-grey-5 text-black text-uppercase",
        "expand-icon-class": "text-black"
      }, {
        default: _withCtx(() => [
          (_unref(users).length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, "Aucun utilisateur"))
            : _createCommentVNode("v-if", true),
          (_unref(users).length > 0 && __props.status !== _unref(UserStatusEnum).VALIDATING)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_component_q_select, {
                  modelValue: selectedRoleFilter.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedRoleFilter).value = $event)),
                  options: roleList.value,
                  "map-options": "",
                  "label-color": _ctx.$q.dark.isActive ? 'white' : 'black',
                  class: "q-mx-sm",
                  "options-dense": "",
                  behavior: "menu",
                  "label-slot": ""
                }, {
                  label: _withCtx(() => [
                    _createTextVNode(" Filtre sur le rôle (# : rôle sélectionné) : ")
                  ]),
                  option: _withCtx((scope) => [
                    _createVNode(_component_q_item, _mergeProps(scope.itemProps, {
                      class: {
                'q-item-focused': scope.focused,
                'q-item-selected': scope.selected && !scope.focused,
              }
                    }), {
                      default: _withCtx(() => [
                        _createVNode(_component_q_item_section, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_label, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(scope.selected ? '# ' : '') + _toDisplayString(scope.opt.label), 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */)
                          ]),
                          _: 2 /* DYNAMIC */
                        }, 1024 /* DYNAMIC_SLOTS */)
                      ]),
                      _: 2 /* DYNAMIC */
                    }, 1040 /* FULL_PROPS, DYNAMIC_SLOTS */, ["class"])
                  ]),
                  _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "options", "label-color"])
              ]))
            : _createCommentVNode("v-if", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(users), (user, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
              _withDirectives(_createVNode(_component_q_item, null, {
                default: _withCtx(() => [
                  _createVNode(_component_q_item_section, { top: "" }, {
                    default: _withCtx(() => [
                      _createCommentVNode(" Username "),
                      _createVNode(_component_q_item_label, {
                        lines: "1",
                        class: "flex items-center no-wrap"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_icon, {
                            size: "xs",
                            name: "person",
                            class: "q-mr-xs"
                          }),
                          _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("span", _hoisted_5, _toDisplayString(user.username ?? _unref($t)('users.unknown')), 1 /* TEXT */),
                            (connectedUser.value?.email === user.email)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_6, " (utilisateur connecté)"))
                              : _createCommentVNode("v-if", true)
                          ])
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */),
                      _createCommentVNode(" Email "),
                      _createVNode(_component_q_item_label, {
                        lines: "1",
                        class: "flex items-center no-wrap"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_icon, {
                            size: "xs",
                            name: "email",
                            class: "q-mr-xs"
                          }),
                          _createElementVNode("span", _hoisted_7, _toDisplayString(user.email ?? _unref($t)('users.unknown')), 1 /* TEXT */)
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */),
                      _createCommentVNode(" Date : signup "),
                      (__props.status == _unref(UserStatusEnum).VALIDATED || __props.status == _unref(UserStatusEnum).VALIDATING)
                        ? (_openBlock(), _createBlock(_component_q_item_label, {
                            key: 0,
                            lines: "1",
                            class: "text-weight-medium custom-caption flex items-center no-wrap word-wrap"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_icon, {
                                size: "xs",
                                name: "calendar_month",
                                class: "q-mr-xs"
                              }),
                              _createTextVNode(" " + _toDisplayString(formatSignupUser(user.signupAt)), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */))
                        : _createCommentVNode("v-if", true),
                      _createCommentVNode(" Date : last connection "),
                      (__props.status !== _unref(UserStatusEnum).VALIDATED && __props.status !== _unref(UserStatusEnum).VALIDATING)
                        ? (_openBlock(), _createBlock(_component_q_item_label, {
                            key: 1,
                            lines: "1",
                            class: "text-weight-medium custom-caption flex items-center no-wrap word-wrap"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_icon, {
                                size: "xs",
                                name: "calendar_month",
                                class: "q-mr-xs"
                              }),
                              _createTextVNode(" " + _toDisplayString(formatUserDate(user.newConnectionAt)), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */))
                        : _createCommentVNode("v-if", true),
                      _createCommentVNode(" Date : signup link expiration "),
                      (__props.status == _unref(UserStatusEnum).VALIDATING)
                        ? (_openBlock(), _createBlock(_component_q_item_label, {
                            key: 2,
                            lines: "1",
                            class: "text-weight-medium custom-caption row items-center no-wrap word-wrap"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_icon, {
                                size: "xs",
                                name: "calendar_month",
                                class: "q-mr-xs"
                              }),
                              _createElementVNode("span", null, _toDisplayString(formatSignupLinkExpiration(user.signupTokenIssuedAt)), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */))
                        : _createCommentVNode("v-if", true)
                    ]),
                    _: 2 /* DYNAMIC */
                  }, 1024 /* DYNAMIC_SLOTS */),
                  _createVNode(_component_q_item_section, { side: "" }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_8, [
                        _createCommentVNode(" Desktop version "),
                        _createCommentVNode(" ACCEPT SIGNUP "),
                        (
                  __props.status == _unref(UserStatusEnum).VALIDATED &&
                  !_unref(isMobile) &&
                  (isSuperAdmin.value || canValidateUser.value)
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 0,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowAcceptDialog(user.email)), ["stop"]),
                              icon: "check",
                              title: 
                  _unref($t)('users.actions.acceptSignup.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" REJECT SIGNUP "),
                        (
                  __props.status == _unref(UserStatusEnum).VALIDATED &&
                  !_unref(isMobile) &&
                  (isSuperAdmin.value || canValidateUser.value)
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 1,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowRejectDialog(user.email)), ["stop"]),
                              icon: "close",
                              title: 
                  _unref($t)('users.actions.rejectSignup.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" RESEND VALIDATION LINK "),
                        (
                  __props.status == _unref(UserStatusEnum).VALIDATING &&
                  !_unref(isMobile) &&
                  (isSuperAdmin.value || canValidateUser.value)
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 2,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowResendValidationLink(user.email)), ["stop"]),
                              icon: "forward_to_inbox",
                              title: 
                  _unref($t)('users.actions.resendValidationLink.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" DELETE "),
                        (
                  __props.status !== _unref(UserStatusEnum).VALIDATED &&
                  !_unref(isMobile) &&
                  isSuperAdmin.value &&
                  (connectedUser.value == null ||
                    (connectedUser.value != null &&
                      user.email !== connectedUser.value.email))
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 3,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              icon: "delete",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowDeleteUserDialog(user.email)), ["stop"]),
                              title: 
                  _unref($t)('users.actions.delete.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" UPDATE INFOS "),
                        (
                  __props.status !== _unref(UserStatusEnum).VALIDATED &&
                  !_unref(isMobile) &&
                  (isSuperAdmin.value ||
                    (isAdmin.value &&
                      ((!isUserAdmin(user) && !isUserSuperAdmin(user)) ||
                        user.email === connectedUser.value?.email)))
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 4,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowUpdateDialog(user.email)), ["stop"]),
                              icon: "edit",
                              title: 
                  _unref($t)('users.actions.updateInfos.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" GENERATE NEW PASSWORD INFOS "),
                        (
                  __props.status !== _unref(UserStatusEnum).VALIDATED &&
                  __props.status !== _unref(UserStatusEnum).VALIDATING &&
                  !_unref(isMobile) &&
                  (isSuperAdmin.value ||
                    (isAdmin.value &&
                      ((!isUserAdmin(user) && !isUserSuperAdmin(user)) ||
                        user.email === connectedUser.value?.email)))
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 5,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowGeneratePasswordDialog(user.email)), ["stop"]),
                              icon: "password",
                              title: 
                  _unref($t)('users.actions.generatePassword.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" UPDATE ROLES "),
                        (
                  __props.status !== _unref(UserStatusEnum).VALIDATED &&
                  __props.status !== _unref(UserStatusEnum).VALIDATING &&
                  !_unref(isMobile) &&
                  (isSuperAdmin.value ||
                    (isAdmin.value &&
                      ((!isUserAdmin(user) && !isUserSuperAdmin(user)) ||
                        user.email === connectedUser.value?.email)))
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 6,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              class: "set-outline",
                              disable: hasToDisable.value,
                              onClick: _withModifiers(($event: any) => (onShowRolesDialog(user.email)), ["stop"]),
                              icon: "manage_accounts",
                              title: 
                  _unref($t)('users.actions.updateRoles.title', {
                    username: user.username,
                  })
                
                            }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                          : _createCommentVNode("v-if", true),
                        _createCommentVNode(" Mobile version "),
                        (
                  _unref(isMobile) &&
                  (isSuperAdmin.value ||
                    canValidateUser.value ||
                    (isAdmin.value &&
                      ((!isUserAdmin(user) && !isUserSuperAdmin(user)) ||
                        user.email === connectedUser.value?.email)))
                )
                          ? (_openBlock(), _createBlock(_component_q_btn, {
                              key: 7,
                              size: "12px",
                              flat: "",
                              dense: "",
                              round: "",
                              disable: hasToDisable.value,
                              icon: "more_vert",
                              onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_menu, {
                                  anchor: "center right",
                                  self: "bottom right",
                                  "auto-close": ""
                                }, {
                                  default: _withCtx(() => [
                                    _createCommentVNode(" MENU TITLE "),
                                    _createVNode(_component_q_item, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_q_item_section, { avatar: "" }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("span", null, "Utilisateur '" + _toDisplayString(user.username) + "'", 1 /* TEXT */)
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */)
                                      ]),
                                      _: 2 /* DYNAMIC */
                                    }, 1024 /* DYNAMIC_SLOTS */),
                                    _createCommentVNode(" ACCEPT "),
                                    (
                      __props.status == _unref(UserStatusEnum).VALIDATED &&
                      (isSuperAdmin.value || canValidateUser.value)
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 0,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "check" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowAcceptDialog(user.email)),
                                              title: 
                        _unref($t)('users.actions.acceptSignup.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.acceptSignup.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true),
                                    _createCommentVNode(" REJECT "),
                                    (
                      __props.status == _unref(UserStatusEnum).VALIDATED &&
                      (isSuperAdmin.value || canValidateUser.value)
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 1,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "close" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowRejectDialog(user.email)),
                                              title: 
                        _unref($t)('users.actions.rejectSignup.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.rejectSignup.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true),
                                    _createCommentVNode(" RESEND VALIDATION LINK "),
                                    (
                      __props.status == _unref(UserStatusEnum).VALIDATING &&
                      (isSuperAdmin.value || canValidateUser.value)
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 2,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "forward_to_inbox" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowResendValidationLink(user.email)),
                                              title: 
                        _unref($t)('users.actions.resendValidationLink.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.resendValidationLink.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true),
                                    _createCommentVNode(" DELETE "),
                                    (
                      __props.status !== _unref(UserStatusEnum).VALIDATED &&
                      isSuperAdmin.value &&
                      (connectedUser.value == null ||
                        (connectedUser.value != null &&
                          user.email !== connectedUser.value.email))
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 3,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "delete" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowDeleteUserDialog(user.email)),
                                              title: 
                        _unref($t)('users.actions.delete.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.delete.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true),
                                    _createCommentVNode(" UPDATE INFOS "),
                                    (
                      __props.status !== _unref(UserStatusEnum).VALIDATED &&
                      (isSuperAdmin.value || isAdmin.value)
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 4,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "edit" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowUpdateDialog(user.email)),
                                              title: 
                        _unref($t)('users.actions.updateInfos.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.updateInfos.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true),
                                    _createCommentVNode(" GENERATE PASSWORD "),
                                    (
                      __props.status !== _unref(UserStatusEnum).VALIDATED &&
                      __props.status !== _unref(UserStatusEnum).VALIDATING &&
                      (isSuperAdmin.value || isAdmin.value)
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 5,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "password" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowGeneratePasswordDialog(user.email)),
                                              title: 
                        _unref($t)('users.actions.generatePassword.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.generatePassword.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true),
                                    _createCommentVNode(" UPDATE ROLES "),
                                    (
                      __props.status !== _unref(UserStatusEnum).VALIDATED &&
                      __props.status !== _unref(UserStatusEnum).VALIDATING &&
                      (isSuperAdmin.value || isAdmin.value)
                    )
                                      ? (_openBlock(), _createBlock(_component_q_item, {
                                          key: 6,
                                          clickable: ""
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_q_item_section, { avatar: "" }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_q_icon, { name: "manage_accounts" })
                                              ]),
                                              _: 1 /* STABLE */
                                            }),
                                            _createVNode(_component_q_item_section, {
                                              onClick: ($event: any) => (onShowRolesDialog(user.email)),
                                              title: 
                        _unref($t)('users.actions.updateRoles.title', {
                          username: user.username,
                        })
                      
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref($t)('users.actions.updateRoles.btnLabel')), 1 /* TEXT */)
                                              ]),
                                              _: 2 /* DYNAMIC */
                                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                          ]),
                                          _: 2 /* DYNAMIC */
                                        }, 1024 /* DYNAMIC_SLOTS */))
                                      : _createCommentVNode("v-if", true)
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disable"]))
                          : _createCommentVNode("v-if", true)
                      ])
                    ]),
                    _: 2 /* DYNAMIC */
                  }, 1024 /* DYNAMIC_SLOTS */)
                ]),
                _: 2 /* DYNAMIC */
              }, 1536 /* NEED_PATCH, DYNAMIC_SLOTS */), [
                [_vShow, 
            selectedRoleFilter.value?.value == null ||
            (user.roles != null &&
              selectedRoleFilter.value.value != null &&
              user.roles.includes(selectedRoleFilter.value.value))
          ]
              ]),
              (index < _unref(users).length - 1)
                ? (_openBlock(), _createBlock(_component_q_separator, { key: 0 }))
                : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */))
          }), 128 /* KEYED_FRAGMENT */))
        ]),
        _: 1 /* STABLE */
      }, 8 /* PROPS */, ["label"])
    ]),
    _: 1 /* STABLE */
  }))
}
}

})